import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card,Empty, Table, Tooltip, Tag, Button } from 'antd';
import { useParams } from 'react-router-dom';
import { Calendar, MapPin } from 'lucide-react';
import moment from 'moment';
import _ from 'lodash';

import usecustomStyles from '../../Common/customStyles';
import './VehicleProfile.css';
import '../../assets/scss/_custom.scss';
import * as VThunk from '../../slices/vehicles/thunk';
import VehicleLiveViewer from '../../pages/VehicleLiveViewer';
import { coordInRegion } from '../../Libs/echarts';
import Crane3DViewer from '../3D/CraneModel';

const customStyles = usecustomStyles();

const VehicleProfile = () => {
    document.title = process.env.REACT_APP_PAGE_TITLE;

    const vehicleLogList = useSelector(state => state.Vehicles.logList);
    const vehicle = useSelector(state => state.Vehicles.vehicle);
    const lastStatus = useSelector(state => state.Vehicles.lastestStatusByVehicleId || {});
    const [lastLocation, setLastLocation] = useState('');
    const [lastOnline, setLastOnline] = useState('');
    const [mapUrl, setMapUrl] = useState('');
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});

    const handleChange = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const allLogs = () => {
        dispatch(VThunk.getLogs(vehicleId));
    };

    const { vehicleId } = useParams();

    const AlarmEventColumns = [
        {
            title: '日志类型',
            width: 100,
            align: 'center',
            render: item => {
                if (item.op === 'A') return <Tag color="#f5222d">警报</Tag>;
                if (item.op === 'O') return <Tag color="#faad14">倾覆</Tag>;
                if (item.op === 'S') return <Tag color="#52c41a">状态</Tag>;
                if (item.op === 'B') return <Tag color="#fa8c16">侵限</Tag>;
                return <Tag color="#d9d9d9">心跳</Tag>;
            }
        },
        {
            title: '时间',
            width: 180,
            align: 'center',
            render: item => moment(item.timestamp).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: '日志内容',
            width: 400,
            dataIndex: 'data',
            key: 'data',
            align: 'left',
            filters: [
                { text: '离开监督范围', value: '离开监督范围' },
                { text: '进入危险区域', value: '进入危险区域' },
            ],
            filteredValue: filteredInfo.data || null,
            onFilter: (value, record) => record.data.includes(value),
            sortOrder: sortedInfo.columnKey === 'data' ? sortedInfo.order : null,
            render: (text, record) => {
                // 根据操作类型显示不同的提示
                let tooltipText = '';
                if (record.op === 'S') {
                    tooltipText = 'f: 风速, l: 力矩, z: 重量, b: 臂长, j: 角度, x: 旋转角度';
                } else if (record.op === 'B') {
                    tooltipText = 'd: 方向, jz: 基准角度, dq: 当前角度, py: 偏移角度, zyz: 左阈值, yyz: 右阈值';
                }
                
                return (
                    <Tooltip title={tooltipText} placement="topLeft">
                        <span style={{ cursor: 'help' }}>{text}</span>
                    </Tooltip>
                );
            }
        }
    ];

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(VThunk.getById(vehicleId));
        dispatch(VThunk.getLastestStatusByVehicleId(vehicleId));
    }, []);

    useEffect(() => {
        if (!_.isEmpty(vehicleId)) {
            dispatch(VThunk.getLogsPartial(vehicleId));
        }
        if (vehicle?.deviceSerial) {
            dispatch(VThunk.getYs7LiveAddr(vehicle.deviceSerial));
        }
    }, [vehicle]);

    useEffect(() => {
        const rmc = ((vehicle || {}).lastHeartbeat || {}).rmc;
        const name = vehicle.licensePlate;
        if (_.isEmpty(rmc) || _.isEmpty(name)) return;

        const region = coordInRegion(rmc.longitude, rmc.latitude);
        setLastLocation(region ? region.name : '');
        setLastOnline(`${rmc.datetime}`);
        const position = [rmc.longitude, rmc.latitude].join(',');
        setMapUrl(`https://uri.amap.com/marker?position=${position}&name=${name}&coordinate=gaode&callnative=0`);
    }, [vehicle]);

    // 基础信息项组件
    const InfoItem = ({ label, value }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',  // 进一步减小padding
            backgroundColor: '#f5f7fa',
            borderRadius: '4px',
            minHeight: '32px'    // 设置最小高度，避免太矮
        }}>
            <span style={{ color: '#8c8c8c', fontSize: '11px', minWidth: '60px' }}>{label}</span>
            <span style={{ color: '#333', fontWeight: 500, fontSize: '12px' }}>{value || '---'}</span>
        </div>
    );

    // 基础信息区域
    const ProfileSection = (
        <Card title="基本信息" styles={{ header: { padding: '8px 14px' } }}>
            <Row gutter={[100, 10]}>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="车牌号" value={vehicle.licensePlate} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="所属项目" value={vehicle.projectName} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="所属工地" value={vehicle.siteName} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="工地负责人" value={vehicle.siteOwnerName} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="品牌" value={vehicle.brand} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="类型" value={vehicle.type} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="最大起重重量" value={vehicle.maxLoad} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <InfoItem label="进场时间" value={vehicle.entryDate} />
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 12px',
                        backgroundColor: '#e6f7ff',
                        borderRadius: '6px'
                    }}>
                        <MapPin size={14} style={{ color: '#1890ff', marginRight: '8px' }} />
                        <span style={{ color: '#8c8c8c', fontSize: '13px', minWidth: '60px' }}>定位</span>
                        <span style={{ color: '#333', fontWeight: 500 }}>{lastLocation || '---'}</span>
                    </div>
                </Col>
                <Col xs={6} sm={6} md={6} lg={6}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 12px',
                        backgroundColor: '#e6f7ff',
                        borderRadius: '6px'
                    }}>
                        <Calendar size={14} style={{ color: '#1890ff', marginRight: '8px' }} />
                        <span style={{ color: '#8c8c8c', fontSize: '13px', minWidth: '60px' }}>最后在线</span>
                        <span style={{ color: '#333', fontWeight: 500 }}>{lastOnline ? moment(lastOnline).format('YYYY-MM-DD HH:mm:ss') : '---'}</span>
                    </div>
                </Col>
                <Col xs={12} sm={12} md={12} lg={12}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 12px',
                        backgroundColor: '#f6ffed',
                        borderRadius: '6px'
                    }}>
                        <span style={{ color: '#52c41a', fontSize: '13px', minWidth: '80px' }}>人员信息</span>
                        <span style={{ color: '#333', fontWeight: 500 }}>
                            {((vehicle || {}).workers || []).map(x => `${x.role}: ${x.name}`).join(', ') || '---'}
                        </span>
                    </div>
                </Col>
            </Row>
        </Card>
    );

    const pagination = {
        pageSizeOptions: ['10', '20', '50'],
        defaultPageSize: 10,
        showSizeChanger: true,
        size: 'small',
    };

    return (
            <div className="vehicle-profile-container">
                {/* 上面：基础信息 */}
                <div style={{ flexShrink: 0 }}>
                    {ProfileSection}
                </div>

                {/* 下面：三个区域 */}
                <div className="content-area">
                    {/* 左侧：视频 + 定位 */}
                    <div style={{ width: '25%', display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>
                        <div style={{ flex: 1, backgroundColor: '#859cba', borderRadius: '8px', overflow: 'hidden' }}>
                            <VehicleLiveViewer />
                        </div>
                        <div style={{ flex: 1, backgroundColor: '#859cba', borderRadius: '8px', overflow: 'hidden' }}>
                            <iframe title="车辆定位" src={mapUrl} width="100%" height="100%" style={{ border: 'none', minHeight: '160px' }} />
                        </div>
                    </div>

                    {/* 中间：预留区域 */}
                    <div style={{ flex: 2, backgroundColor: '#fafafa', borderRadius: '8px', overflow: 'hidden' }}>
                        <Crane3DViewer
                            vehicleId={vehicleId}
                            vehicleStatus={lastStatus}
                        />
                    </div>

                    {/* 右侧：事件日志 */}
                    <Card
                        title={
                            '事件日志'
                        }
                        extra={
                            <Button
                                type="primary"
                                size="small"
                                onClick={allLogs}
                            >
                                查看全部
                            </Button>
                        }
                        style={{ 
                            width: '25%', 
                            height: '100%', 
                            flexShrink: 0,
                            // backgroundColor: '#1f1f1f',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        bodyStyle={{ 
                            padding: 0,           // 移除默认 padding
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="custom-scrollbar" style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '2px' }}>
                            <Table
                                className='table-list'
                                rowKey="id"
                                columns={AlarmEventColumns}
                                dataSource={vehicleLogList}
                                pagination={pagination}
                                onChange={handleChange}
                                scroll={{ x: 'auto' }}
                                style={{ backgroundColor: 'transparent' }}
                                components={{
                                    header: {
                                        cell: props => <th {...props} style={{ color: '#999', borderBottom: '1px solid #303030', padding: '6px 10px', fontSize: '12px', whiteSpace: 'nowrap' }} />
                                    },
                                    body: {
                                        row: props => <tr {...props} style={{ borderBottom: '1px solid #262626' }} />,
                                        cell: props => <td {...props} style={{ color: '#fff', padding: '8px 10px', fontSize: '12px', whiteSpace: 'nowrap' }} />
                                    }
                                }}
                            />
                        </div>                                  
                    </Card>
                </div>
            </div>
    );
};

export default VehicleProfile;